<script setup>
import { ref, watch, onMounted } from 'vue'
import draggable from 'vuedraggable'
import MediaTreeNode from './MediaTreeNode.vue'

// Read config from mount element data attributes
const el = document.getElementById('media-tree-app')
const config = {
    groups:             JSON.parse(el.dataset.groups || '{}'),
    categorytreeUrl:    (el.dataset.categorytreeUrl || '').replace(/\/$/, ''),
    treeitemsUrl:       (el.dataset.treeitemsUrl || '').replace(/\/$/, ''),
    changecategoryUrl:  el.dataset.changecategoryUrl || '',
    savesortkeysUrl:    el.dataset.savesortkeysUrl || '',
    baseUrl:            (el.dataset.baseUrl || '').replace(/\/$/, ''),
    securityId:         el.dataset.securityId || '',
}

const icons = {
    pencil: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/><path d="m15 5 4 4"/></svg>',
    copy: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>',
    trash: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>',
}

const storedGroup    = parseInt(localStorage.getItem('mediaTree.selectedGroup')) || 0
const selectedGroup  = ref(parseInt(el.dataset.selectedGroup) || storedGroup)
const rawCat = el.dataset.selectedCat
const selectedCat    = ref(rawCat !== '' && rawCat !== undefined ? parseInt(rawCat) : null)
const treeData       = ref([])
const items          = ref([])
const currentPage    = ref(1)
const totalItems     = ref(0)
const perPage        = 20
const loadingTree    = ref(false)
const loadingItems   = ref(false)
const draggingItemId = ref(null)
const dragOverCatId  = ref(null)
const currentUrl     = ref(window.location.href)

// --- Data loading ---

async function loadTree() {
    if (!selectedGroup.value) { treeData.value = []; return }
    loadingTree.value = true
    try {
        const r = await fetch(`${config.categorytreeUrl}/${selectedGroup.value}`)
        if (!r.ok) throw new Error('HTTP ' + r.status)
        const data = await r.json()
        treeData.value = data.categories || []
    } catch (e) {
        console.error('Tree load error:', e)
        treeData.value = []
    } finally {
        loadingTree.value = false
    }
}

async function loadItems() {
    if (selectedCat.value === null) { items.value = []; totalItems.value = 0; return }
    loadingItems.value = true
    try {
        const extra = selectedCat.value === 0 ? `&group=${selectedGroup.value}` : ''
        const r = await fetch(`${config.treeitemsUrl}?cat=${selectedCat.value}&page=${currentPage.value}${extra}`)
        if (!r.ok) throw new Error('HTTP ' + r.status)
        const data = await r.json()
        items.value   = data.items
        totalItems.value = data.total
    } catch (e) {
        console.error('Items load error:', e)
        items.value = []
        totalItems.value = 0
    } finally {
        loadingItems.value = false
    }
}

function totalPages() {
    return Math.ceil(totalItems.value / perPage)
}

async function goToPage(p) {
    const max = totalPages()
    if (p < 1 || p > max) return
    currentPage.value = p
    await loadItems()
}

// --- Watchers & lifecycle ---

watch(selectedGroup, (val) => {
    selectedCat.value = null
    items.value = []
    totalItems.value = 0
    currentPage.value = 1
    if (val) localStorage.setItem('mediaTree.selectedGroup', val)
    loadTree()
    syncUrl()
})

watch(selectedCat, (val) => {
    currentPage.value = 1
    loadItems()
    syncUrl()
})

function syncUrl() {
    const url = new URL(window.location)
    if (selectedGroup.value) {
        url.searchParams.set('group', selectedGroup.value)
    } else {
        url.searchParams.delete('group')
    }
    if (selectedCat.value !== null) {
        url.searchParams.set('cat', selectedCat.value)
    } else {
        url.searchParams.delete('cat')
    }
    window.history.replaceState({}, '', url)
    currentUrl.value = url.toString()
}

onMounted(() => {
    if (selectedGroup.value) loadTree()
    if (selectedCat.value !== null) loadItems()
})

// --- Drag: list reorder (vuedraggable) ---

async function onReorder() {
    const catId  = selectedCat.value
    const offset = (currentPage.value - 1) * perPage
    const payload = items.value.map((item, i) => ({
        itemId: item.id,
        catId,
        sortKey: String(offset + i + 1),
    }))
    // Update local state immediately
    items.value.forEach((item, i) => { item.sortKey = String(offset + i + 1) })
    try {
        await fetch(config.savesortkeysUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                items: JSON.stringify(payload),
                SecurityID: config.securityId,
            }).toString(),
        })
    } catch (e) {
        console.error('SortKey save error:', e)
    }
}

// --- Drag: item row → tree node ---

function onItemDragStart(itemId) {
    draggingItemId.value = itemId
}

function onItemDragEnd() {
    draggingItemId.value = null
    dragOverCatId.value = null
}

function onCatDragOver(catId) {
    if (draggingItemId.value) dragOverCatId.value = catId
}

function onCatDragLeave(catId) {
    if (dragOverCatId.value === catId) dragOverCatId.value = null
}

async function onDropToCategory(newCatId) {
    const itemId = draggingItemId.value
    draggingItemId.value = null
    dragOverCatId.value = null

    if (!itemId || newCatId === selectedCat.value) return

    try {
        const r = await fetch(config.changecategoryUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                itemId,
                newCatId,
                oldCatId: selectedCat.value ?? 0,
                SecurityID: config.securityId,
            }).toString(),
        })
        if (!r.ok) throw new Error('HTTP ' + r.status)
        // Reload tree (counts change) and item list
        await Promise.all([loadTree(), loadItems()])
    } catch (e) {
        console.error('Category change error:', e)
    }
}

// --- Delete with confirmation ---
function deleteItem(item) {
    if (confirm('Sind Sie sicher?')) {
        window.location.href = `${config.baseUrl}/delete/${item.id}?BackURL=${encodeURIComponent(currentUrl.value)}`
    }
}
</script>

<template>
    <div class="media-tree-layout">

        <!-- Left: group selector + category tree -->
        <div class="media-tree-sidebar">
            <select v-model="selectedGroup" class="select select-sm w-full mb-2">
                <option :value="0">— Gruppe wählen —</option>
                <option v-for="(title, id) in config.groups" :key="id" :value="parseInt(id)">
                    {{ title }}
                </option>
            </select>

            <div v-if="loadingTree" class="opacity-60 mt-2">Lade…</div>
            <div v-else-if="!selectedGroup" class="opacity-60 mt-2">Gruppe auswählen…</div>
            <ul v-else-if="treeData.length" class="cat-tree mt-4">
                <MediaTreeNode
                    v-for="cat in treeData"
                    :key="cat.id"
                    :cat="cat"
                    :selected-cat="selectedCat"
                    :drag-over-cat-id="dragOverCatId"
                    :is-dragging="draggingItemId !== null"
                    @select="selectedCat = $event"
                    @dragover="onCatDragOver"
                    @dragleave="onCatDragLeave"
                    @drop="onDropToCategory"
                />
            </ul>
            <div v-else class="opacity-60 mt-2">Keine Kategorien vorhanden.</div>
        </div>

        <!-- Right: sortable item list -->
        <div class="media-tree-content">
            <div v-if="loadingItems" class="opacity-60">Lade…</div>
            <div v-else-if="selectedCat === null" class="opacity-60 mt-4">Kategorie im Baum auswählen.</div>
            <template v-else>
                <table class="table table-zebra table-sm w-full">
                    <thead>
                        <tr>
                            <th style="width:28px"></th>
                            <th>Thumbnail</th>
                            <th>ID</th>
                            <th>Titel / Beschreibung</th>
                            <th>Datierung</th>
                            <th>Ausst. Status</th>
                            <th>Kategorien</th>
                            <th></th><th></th><th></th>
                        </tr>
                    </thead>
                    <draggable
                        tag="tbody"
                        v-model="items"
                        item-key="id"
                        handle=".drag-handle"
                        @end="onReorder"
                    >
                        <template #item="{ element }">
                            <tr
                                draggable="true"
                                @dragstart="onItemDragStart(element.id)"
                                @dragend="onItemDragEnd"
                            >
                                <td class="drag-handle w-8" title="Ziehen zum Sortieren oder auf Kategorie">⠿</td>
                                <td class="w-8">
                                    <a :href="element.viewUrl">
                                        <img v-if="element.thumbUrl" :src="element.thumbUrl" style="max-height:48px; max-width:64px;">
                                        <span v-else class="opacity-60 text-sm">—</span>
                                    </a>
                                </td>
                                <td class="w-8">
                                    <a :href="element.viewUrl">{{ element.id }}</a>
                                </td>
                                <td>
                                    <a :href="element.viewUrl">
                                        <strong>{{ element.title }}</strong>
                                        <span class="opacity-60"> ({{ element.renderType }})</span>
                                    </a>
                                    <p v-if="element.description" class="m-0 text-sm">{{ element.description }}</p>
                                </td>
                                <td>{{ element.datePrecise }}</td>
                                <td>{{ element.exhibitionStatus }}</td>
                                <td>
                                    <a
                                        v-for="cat in element.categories.filter(c => c.groupId === selectedGroup)"
                                        :key="cat.code"
                                        class="badge badge-neutral badge-sm me-1"
                                        :title="cat.title"
                                    >{{ cat.code }}</a>
                                </td>
                                <template v-if="element.canEdit">
                                    <td class="w-8">
                                        <a :href="`${config.baseUrl}/edit/${element.id}?BackURL=${encodeURIComponent(currentUrl)}`" class="btn btn-ghost btn-xs btn-circle" title="Bearbeiten" v-html="icons.pencil"></a>
                                    </td>
                                    <td class="w-8">
                                        <a :href="`${config.baseUrl}/duplicate/${element.id}?BackURL=${encodeURIComponent(currentUrl)}`" class="btn btn-ghost btn-xs btn-circle" title="Duplizieren"
                                           v-html="icons.copy"></a>
                                    </td>
                                    <td class="w-8">
                                        <a href="#" class="btn btn-ghost btn-xs btn-circle" title="Löschen" v-html="icons.trash"
                                           @click.prevent="deleteItem(element)"></a>
                                    </td>
                                </template>
                                <template v-else>
                                    <td></td><td></td><td></td>
                                </template>
                            </tr>
                        </template>
                        <template #footer>
                            <tr v-if="!loadingItems && items.length === 0">
                                <td colspan="10" class="opacity-60">Keine Einträge in dieser Kategorie.</td>
                            </tr>
                        </template>
                    </draggable>
                </table>
                <!-- Pagination -->
                <div v-if="totalPages() > 1" class="join mt-2">
                    <button class="join-item btn btn-sm" :disabled="currentPage === 1"
                            @click.prevent="goToPage(currentPage - 1)">«</button>
                    <button v-for="p in totalPages()" :key="p"
                            class="join-item btn btn-sm" :class="{ 'btn-primary': p === currentPage }"
                            @click.prevent="goToPage(p)">{{ p }}</button>
                    <button class="join-item btn btn-sm" :disabled="currentPage === totalPages()"
                            @click.prevent="goToPage(currentPage + 1)">»</button>
                </div>
            </template>
        </div>

    </div>
</template>
